import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from "recharts";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import QuotationTypeToggle from "@/components/QuotationTypeToggle";
import { useQuotationTypeView, matchesQuotationType } from "@/hooks/useQuotationTypeView";
import MultiSelectChips from "@/components/MultiSelectChips";
import { useRemarkCategories } from "@/hooks/useRemarkCategories";
import { useTableSort, SortableHeader } from "@/lib/tableSort";

// Category color palette — consistent with brand pill aesthetic.
const CATEGORY_COLORS = [
  "#32B4AC", "#7A5C00", "#0C447C", "#085041", "#7A3000",
  "#A32D2D", "#27500A", "#5B21B6", "#475569",
];

const formatCurrency = (v) => {
  if (v == null) return "—";
  if (v >= 1e7) return `₹${(v/1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v/1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v/1e3).toFixed(1)} K`;
  return `₹${v.toLocaleString("en-IN")}`;
};

export default function RemarksAnalytics() {
  const [allQuotations, setAllQuotations] = useState([]);
  const [allSellers, setAllSellers] = useState([]);
  const [loading, setLoading] = useState(true);

  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  const today = now.toISOString().substring(0, 10);
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  const [typeView] = useQuotationTypeView();

  const allCategories = useRemarkCategories();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sellerSearch, setSellerSearch] = useState("");
  const [granularity, setGranularity] = useState("day"); // day | week | month

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    setLoading(true);
    try {
      const [qRes, sRes] = await Promise.all([
        axios.get(`${API}/quotations`, { withCredentials: true }),
        axios.get(`${API}/sellers`, { withCredentials: true }),
      ]);
      setAllQuotations(qRes.data || []);
      setAllSellers(sRes.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const sellerMap = useMemo(() => {
    const m = {};
    allSellers.forEach((s) => { m[s.id] = s; });
    return m;
  }, [allSellers]);

  // Core filtered universe: invoice uploaded, valid invoice_date, type-toggle + date range.
  // Category filter is applied SEPARATELY in each section so the summary cards still reflect
  // the broader population, and only the distribution / table / trend respect the category filter.
  const baseQuotations = useMemo(() => {
    return (allQuotations || []).filter((q) => {
      if (!q.invoice_file_url) return false;
      const d = (q.invoice_date || "").substring(0, 10);
      if (!d) return false;
      if (!matchesQuotationType(q, typeView)) return false;
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    });
  }, [allQuotations, dateFrom, dateTo, typeView]);

  // Apply the category filter on top. Quotation matches if ANY of its categories is selected.
  const filteredQuotations = useMemo(() => {
    if (selectedCategories.length === 0) return baseQuotations;
    return baseQuotations.filter((q) =>
      (q.remark_categories || []).some((c) => selectedCategories.includes(c))
    );
  }, [baseQuotations, selectedCategories]);

  // ---------- Summary cards ----------
  const summary = useMemo(() => {
    const total = baseQuotations.length;
    const valid = baseQuotations.filter((q) => q.final_status === "valid").length;
    const over = baseQuotations.filter((q) => q.final_status === "over_discount").length;
    // Most common category (counted on the same base population; quotations contribute once per category)
    const counts = {};
    baseQuotations.forEach((q) => (q.remark_categories || []).forEach((c) => { counts[c] = (counts[c] || 0) + 1; }));
    let topCategory = "—";
    let topCount = 0;
    for (const [c, n] of Object.entries(counts)) if (n > topCount) { topCount = n; topCategory = c; }
    const totalCategoryUses = Object.values(counts).reduce((s, n) => s + n, 0);
    return {
      total,
      valid,
      over,
      validPct: total > 0 ? (valid / total) * 100 : 0,
      overPct: total > 0 ? (over / total) * 100 : 0,
      topCategory,
      topCount,
      topPct: totalCategoryUses > 0 ? (topCount / totalCategoryUses) * 100 : 0,
    };
  }, [baseQuotations]);

  // ---------- Distribution data ----------
  const distributionData = useMemo(() => {
    const counts = {};
    filteredQuotations.forEach((q) => (q.remark_categories || []).forEach((c) => { counts[c] = (counts[c] || 0) + 1; }));
    const total = Object.values(counts).reduce((s, n) => s + n, 0);
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value, pct: total > 0 ? (value / total) * 100 : 0 }))
      .sort((a, b) => b.value - a.value);
  }, [filteredQuotations]);

  const CategoryTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-xs" data-testid="remarks-category-tooltip">
        <div className="font-semibold text-slate-900 mb-1">{d.name}</div>
        <div className="text-slate-600">Quotations: <span className="font-mono text-slate-900">{d.value}</span></div>
        <div className="text-slate-600">Contribution: <span className="font-mono text-slate-900">{d.pct.toFixed(1)}%</span></div>
      </div>
    );
  };

  // ---------- Seller-wise table ----------
  const sellerRows = useMemo(() => {
    // Group strictly by seller_id (fall back to a stable key from the seller_name when id is missing
    // for very old rows). This avoids merging two distinct sellers that happen to share a display name.
    const groups = {};
    filteredQuotations.forEach((q) => {
      const id = q.seller_id || `name:${(q.seller_name || "Unknown").trim()}`;
      const displayName = (sellerMap[q.seller_id]?.seller_name || q.seller_name || "Unknown").trim();
      if (!groups[id]) groups[id] = { id, name: displayName, overCount: 0, totalInvoice: 0, effSum: 0, effCount: 0, categoryCounts: {} };
      if (q.final_status === "over_discount") groups[id].overCount += 1;
      groups[id].totalInvoice += Number(q.total_invoice_value) || 0;
      if (q.effective_discount != null) {
        groups[id].effSum += Number(q.effective_discount);
        groups[id].effCount += 1;
      }
      (q.remark_categories || []).forEach((c) => { groups[id].categoryCounts[c] = (groups[id].categoryCounts[c] || 0) + 1; });
    });
    const totalInvoiceAll = Object.values(groups).reduce((s, g) => s + g.totalInvoice, 0);
    return Object.values(groups).map((g) => {
      let topCat = "—";
      let topN = 0;
      for (const [c, n] of Object.entries(g.categoryCounts)) if (n > topN) { topN = n; topCat = c; }
      return {
        id: g.id,
        name: g.name,
        topCategory: topCat,
        overCount: g.overCount,
        avgEffDiscount: g.effCount > 0 ? g.effSum / g.effCount : 0,
        totalInvoice: g.totalInvoice,
        contribution: totalInvoiceAll > 0 ? (g.totalInvoice / totalInvoiceAll) * 100 : 0,
      };
    });
  }, [filteredQuotations, sellerMap]);

  const filteredSellerRows = useMemo(() => {
    const q = sellerSearch.trim().toLowerCase();
    if (!q) return sellerRows;
    return sellerRows.filter((r) => r.name.toLowerCase().includes(q));
  }, [sellerRows, sellerSearch]);

  const { sortedRows: sortedSellerRows, sort: sellerSort, toggleSort: toggleSellerSort } = useTableSort(filteredSellerRows, {
    defaultKey: "overCount",
    defaultOrder: "desc",
    accessors: {
      name: (r) => r.name,
      topCategory: (r) => r.topCategory,
      overCount: (r) => r.overCount,
      avgEffDiscount: (r) => r.avgEffDiscount,
      totalInvoice: (r) => r.totalInvoice,
      contribution: (r) => r.contribution,
    },
  });

  // ---------- Trend over time ----------
  const bucketOf = (iso) => {
    if (!iso) return "";
    if (granularity === "day") return iso.substring(0, 10);
    if (granularity === "month") return iso.substring(0, 7);
    // week: get ISO week start (Mon)
    const dt = new Date(iso);
    const dow = (dt.getDay() + 6) % 7;
    dt.setDate(dt.getDate() - dow);
    return dt.toISOString().substring(0, 10);
  };

  const trendData = useMemo(() => {
    // map: bucket -> { date, [category]: count }
    const m = {};
    const cats = new Set();
    filteredQuotations.forEach((q) => {
      const b = bucketOf(q.invoice_date);
      if (!b) return;
      if (!m[b]) m[b] = { date: b };
      (q.remark_categories || []).forEach((c) => {
        m[b][c] = (m[b][c] || 0) + 1;
        cats.add(c);
      });
    });
    return {
      rows: Object.values(m).sort((a, b) => a.date.localeCompare(b.date)),
      cats: Array.from(cats),
    };
  }, [filteredQuotations, granularity]);

  // ---------- Render ----------
  if (loading) return <div className="text-sm text-muted-foreground">Loading remarks analytics…</div>;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-[#0F0F0F]" data-testid="remarks-analytics-title">Remarks Analytics</h1>
        <p className="text-xs text-muted-foreground">Reasons behind discount overrides across invoiced quotations.</p>
      </div>

      {/* Filter bar */}
      <div className="flex items-center flex-wrap gap-2 p-3 bg-white border border-[#E8E8E8] rounded-md">
        <DateRangeFilter from={dateFrom} to={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} testId="remarks-date-range" />
        <QuotationTypeToggle testId="remarks-qtype" />
        <div className="min-w-[280px]">
          <MultiSelectChips
            options={allCategories}
            value={selectedCategories}
            onChange={setSelectedCategories}
            placeholder="All categories"
            testId="remarks-category-filter"
          />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="remarks-card-valid">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground font-medium">Valid Quotations</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold font-mono">{summary.valid}</div>
            <div className="text-[11px] text-muted-foreground">{summary.validPct.toFixed(1)}% of {summary.total}</div>
          </CardContent>
        </Card>
        <Card data-testid="remarks-card-over">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground font-medium">Over-Discount Quotations</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold font-mono text-red-600">{summary.over}</div>
            <div className="text-[11px] text-muted-foreground">{summary.overPct.toFixed(1)}% of {summary.total}</div>
          </CardContent>
        </Card>
        <Card data-testid="remarks-card-top-category">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground font-medium">Most Common Category</CardTitle></CardHeader>
          <CardContent>
            <div className="text-base font-semibold text-[#085041] truncate" title={summary.topCategory}>{summary.topCategory}</div>
            <div className="text-[11px] text-muted-foreground">{summary.topCount} invoices · {summary.topPct.toFixed(1)}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Distribution donut */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Remarks Distribution</CardTitle></CardHeader>
        <CardContent>
          {distributionData.length === 0 ? (
            <div className="h-[280px] flex items-center justify-center text-xs text-muted-foreground">No quotations with remark categories in this range</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={distributionData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} dataKey="value" paddingAngle={2}>
                      {distributionData.map((d, i) => <Cell key={d.name} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CategoryTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1 text-xs">
                {distributionData.map((d, i) => (
                  <div key={d.name} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-slate-50" data-testid={`remarks-dist-row-${d.name}`}>
                    <div className="flex items-center gap-2 truncate">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }} />
                      <span className="truncate">{d.name}</span>
                    </div>
                    <div className="flex items-center gap-3 font-mono text-slate-700 shrink-0">
                      <span>{d.value}</span>
                      <span className="text-muted-foreground">{d.pct.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trend chart */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold">Remark Trend Over Time</CardTitle>
          <div className="inline-flex items-center rounded-md border border-[#E8E8E8] bg-slate-50 p-0.5" role="tablist" data-testid="remarks-granularity-toggle">
            {["day", "week", "month"].map((g) => (
              <button key={g} type="button" onClick={() => setGranularity(g)} data-testid={`remarks-granularity-${g}`}
                className={`px-2 h-6 text-[11px] capitalize rounded transition-colors ${granularity === g ? "bg-white text-[#0F0F0F] shadow-sm border border-[#E8E8E8]" : "text-[#1A1A1A]/60 hover:text-[#0F0F0F]"}`}>
                {g}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {trendData.rows.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-xs text-muted-foreground">No data for the selected filters</div>
          ) : (
            <div className="h-[260px] min-w-0">
              <ResponsiveContainer width="99%" height="100%">
                <LineChart data={trendData.rows} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#E8E8E8" }} />
                  <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {trendData.cats.map((c, i) => (
                    <Line key={c} type="monotone" dataKey={c} stroke={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Seller-wise table */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-4">
          <CardTitle className="text-base font-semibold">Seller-wise Remarks</CardTitle>
          <div className="relative w-[260px]">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={sellerSearch} onChange={(e) => setSellerSearch(e.target.value)} placeholder="Search seller…" className="h-8 pl-7 text-xs border-[#E8E8E8]" data-testid="remarks-seller-search" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="max-h-[420px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <SortableHeader sortKey="name" sort={sellerSort} onToggle={toggleSellerSort} className="text-xs">Seller</SortableHeader>
                  <SortableHeader sortKey="topCategory" sort={sellerSort} onToggle={toggleSellerSort} className="text-xs">Most Used Category</SortableHeader>
                  <SortableHeader sortKey="overCount" sort={sellerSort} onToggle={toggleSellerSort} align="right" className="text-xs">Over-Discount #</SortableHeader>
                  <SortableHeader sortKey="avgEffDiscount" sort={sellerSort} onToggle={toggleSellerSort} align="right" className="text-xs">Avg. Eff. Discount %</SortableHeader>
                  <SortableHeader sortKey="totalInvoice" sort={sellerSort} onToggle={toggleSellerSort} align="right" className="text-xs">Total Invoice</SortableHeader>
                  <SortableHeader sortKey="contribution" sort={sellerSort} onToggle={toggleSellerSort} align="right" className="text-xs">Contribution %</SortableHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSellerRows.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-6">No sellers match the current filters</TableCell></TableRow>
                ) : sortedSellerRows.map((r) => (
                  <TableRow key={r.id} data-testid={`remarks-seller-row-${r.name}`}>
                    <TableCell className="text-xs">{r.name}</TableCell>
                    <TableCell className="text-xs">
                      {r.topCategory === "—" ? <span className="text-muted-foreground">—</span> : (
                        <span className="inline-flex items-center rounded-full bg-[#E1F5EE] text-[#085041] border border-[#A5DDC9] px-2 py-0.5 text-[11px] font-medium">{r.topCategory}</span>
                      )}
                    </TableCell>
                    <TableCell className={`text-xs text-right font-mono ${r.overCount > 0 ? "text-red-600 font-semibold" : ""}`}>{r.overCount}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{r.avgEffDiscount.toFixed(1)}%</TableCell>
                    <TableCell className="text-xs text-right font-mono">{formatCurrency(r.totalInvoice)}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{r.contribution.toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
